import React, { useState, useEffect } from "react";
import imageCompression from "browser-image-compression";
import {
  Box,
  Typography,
  Snackbar,
  Alert,
  Paper,
  Switch,
  IconButton,
  Divider,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import { NoSuggestionsTextField } from "../../../../components/textFields";
import { CloseButton, SendButton } from "../../../../components/buttons";
import { useNavigate } from "react-router-dom";
import { makeRequest } from "../../../../assets/functions_helper";
import { useSubscriptionUsage } from "../../../../context/SubscriptionUsageContext";
import CustomAutocomplete from "../../../../components/autoComplete";
import {
  CustomDialog,
  CustomDialogActions,
  CustomDialogContent,
  CustomDialogTitle,
} from "../../../../components/customDialog";

function CreateProductDialog({ open, handleClose, restaurantId, fetchProducts }) {
  const { fetchUsage } = useSubscriptionUsage();
  // Estados para Modal
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("error");

  // Estados del Formulario (Básico)
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [posId, setPosId] = useState("");
  const [price, setPrice] = useState("");
  const [displayPrice, setDisplayPrice] = useState("");
  const [discountPrice, setDiscountPrice] = useState("");
  const [displayDiscountPrice, setDisplayDiscountPrice] = useState("");
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [categories, setCategories] = useState([]);

  // Estados del Formulario (Complejo)
  const [isComplex, setIsComplex] = useState(false);
  
  // Estructura: [{ option_display_name: "", details: [{ name: "", value: "" }] }]
  const [detailVariants, setDetailVariants] = useState([
    { option_display_name: "", details: [{ name: "", value: "" }] },
  ]);

  const navigate = useNavigate();

  const showSnackbar = (message, severity = "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    if (open) {
      // Limpiar el estado cada vez que se abre
      setName("");
      setCategory("");
      setDescription("");
      setPosId("");
      setPrice("");
      setDisplayPrice("");
      setDiscountPrice("");
      setDisplayDiscountPrice("");
      setImage(null);
      setPreviewUrl(null);
      setIsComplex(false);
      setDetailVariants([{ option_display_name: "", details: [{ name: "", value: "" }] }]);
    }
  }, [open]);

  useEffect(() => {
    if (!restaurantId || !open) return;
    const fetchCategories = () => {
      makeRequest(
        "GET",
        `menu_manager/categories/${restaurantId}?category_type=categories`,
        null,
        "application/json",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        null,
        () => navigate("/login")
      )
        .then((res) => {
          setCategories(res || []);
        })
        .catch(() => {
          showSnackbar("No se pudieron cargar las categorías", "error");
        });
    };
    fetchCategories();
  }, [restaurantId, open, navigate]);

  const handlePriceChange = (e) => {
    const inputValue = e.target.value.replace(/\D/g, "");
    setPrice(inputValue);
    setDisplayPrice(inputValue ? `$${parseInt(inputValue).toLocaleString("es-CL")}` : "");
  };

  const handleDiscountPriceChange = (e) => {
    const inputValue = e.target.value.replace(/\D/g, "");
    setDiscountPrice(inputValue);
    setDisplayDiscountPrice(inputValue ? `$${parseInt(inputValue).toLocaleString("es-CL")}` : "");
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const maxFileSizeMB = 10;
    const maxFileSizeBytes = maxFileSizeMB * 1024 * 1024;

    if (file.size > maxFileSizeBytes) {
      showSnackbar(`La imagen no debe superar los ${maxFileSizeMB}MB.`);
      return;
    }

    try {
      const options = { maxSizeMB: 1, maxWidthOrHeight: 800, useWebWorker: true };
      const compressedFile = await imageCompression(file, options);
      setImage(compressedFile);
      setPreviewUrl(URL.createObjectURL(compressedFile));
    } catch (error) {
      console.error("Error al comprimir la imagen:", error);
      showSnackbar("Error al comprimir la imagen", "error");
    }
  };

  const handleAddGroup = () => {
    setDetailVariants([
      ...detailVariants,
      { option_display_name: "", details: [{ name: "", value: "" }] },
    ]);
  };

  const handleRemoveGroup = (index) => {
    const newVariants = [...detailVariants];
    newVariants.splice(index, 1);
    setDetailVariants(newVariants);
  };

  const handleGroupTitleChange = (index, value) => {
    const newVariants = [...detailVariants];
    newVariants[index].option_display_name = value;
    setDetailVariants(newVariants);
  };

  const handleAddOption = (groupIndex) => {
    const newVariants = [...detailVariants];
    newVariants[groupIndex].details.push({ name: "", value: "" });
    setDetailVariants(newVariants);
  };

  const handleRemoveOption = (groupIndex, optionIndex) => {
    const newVariants = [...detailVariants];
    newVariants[groupIndex].details.splice(optionIndex, 1);
    setDetailVariants(newVariants);
  };

  const handleOptionChange = (groupIndex, optionIndex, field, value) => {
    const newVariants = [...detailVariants];
    if (field === "value") {
      const rawValue = value.replace(/\D/g, "");
      newVariants[groupIndex].details[optionIndex][field] = rawValue ? parseInt(rawValue).toLocaleString("es-CL") : "";
    } else {
      newVariants[groupIndex].details[optionIndex][field] = value;
    }
    setDetailVariants(newVariants);
  };

  const handleSubmit = async () => {
    // Validaciones Básicas
    if (!name || !category || !price || !posId) {
      showSnackbar("Por favor completa todos los campos obligatorios (incluyendo POS).", "warning");
      return;
    }

    const cleanPrice = Number(price);
    const cleanDiscountPrice = discountPrice ? Number(discountPrice) : null;

    if (isNaN(cleanPrice) || cleanPrice <= 0) {
      showSnackbar("El precio debe ser un valor numérico válido.", "warning");
      return;
    }
    
    if (cleanDiscountPrice && cleanDiscountPrice >= cleanPrice) {
      showSnackbar("El precio con descuento debe ser menor al precio normal.", "warning");
      return;
    }

    // Validaciones de Complejos
    const cleanedVariants = [];
    if (isComplex) {
      for (const group of detailVariants) {
        if (!group.option_display_name.trim()) {
          showSnackbar("Todos los grupos de variantes deben tener un nombre.", "warning");
          return;
        }
        const cleanedDetails = [];
        for (const option of group.details) {
          if (!option.name.trim()) {
            showSnackbar(`La opción en '${group.option_display_name}' no puede estar vacía.`, "warning");
            return;
          }
          let finalValue = null;
          if (option.value && option.value.trim() !== "") {
            const parsedVal = Number(option.value.replace(/\D/g, ""));
            if (isNaN(parsedVal)) {
              showSnackbar(`El costo extra en '${option.name}' debe ser numérico.`, "warning");
              return;
            }
            finalValue = parsedVal;
          }
          cleanedDetails.push({ name: option.name, value: finalValue });
        }
        
        if (cleanedDetails.length === 0) {
           showSnackbar(`El grupo '${group.option_display_name}' debe tener al menos una opción.`, "warning");
           return;
        }

        cleanedVariants.push({
          option_display_name: group.option_display_name,
          details: cleanedDetails,
        });
      }
      
      if (cleanedVariants.length === 0) {
         showSnackbar("Un producto complejo debe tener al menos un grupo de opciones.", "warning");
         return;
      }
    }

    setIsSubmitting(true);

    const payload = {
      name,
      category,
      description,
      price: cleanPrice,
      discounted_price: cleanDiscountPrice,
      is_active: true,
      pos_id: posId,
      is_complex: isComplex,
    };

    if (isComplex) {
      payload.detail_variants = cleanedVariants;
    }

    const formData = new FormData();
    formData.append("new_product", JSON.stringify(payload));
    if (image) {
      formData.append("product_image", image);
    }
    formData.append("restaurant_id", restaurantId);

    try {
      await makeRequest(
        "POST",
        `menu_manager/create_product/`,
        formData,
        "multipart/form-data",
        localStorage.getItem("token"),
        localStorage.getItem("tokenType"),
        null,
        null,
        () => navigate("/login")
      );
      showSnackbar("Producto creado correctamente", "success");
      fetchProducts();
      fetchUsage(restaurantId);
      setTimeout(() => {
        handleClose();
      }, 1000);
    } catch (error) {
      showSnackbar("Error al crear el producto", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <CustomDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <CustomDialogTitle>Crear Producto</CustomDialogTitle>
        <CustomDialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <NoSuggestionsTextField
              autoFocus
              required
              margin="dense"
              id="name"
              label="Nombre del Producto"
              type="text"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
            />

            <CustomAutocomplete
              options={categories.map((c) => ({ id: c.id, label: c.name ?? c.label ?? "" }))}
              value={category}
              onChange={(e, newCategoryId) => setCategory(newCategoryId ?? "")}
              label="Categoría"
              required
              returnLabel
            />

            <NoSuggestionsTextField
              required
              margin="dense"
              id="description"
              label="Descripción"
              type="text"
              fullWidth
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <NoSuggestionsTextField
              required
              margin="dense"
              id="posId"
              label="Código POS"
              type="text"
              fullWidth
              value={posId}
              onChange={(e) => setPosId(e.target.value)}
              helperText="Ej: 1001 (obligatorio)"
            />

            <Box sx={{ display: "flex", gap: 2 }}>
              <NoSuggestionsTextField
                required
                margin="dense"
                id="price"
                label={isComplex ? "Precio Base" : "Precio Normal"}
                type="text"
                fullWidth
                value={displayPrice}
                onChange={handlePriceChange}
              />
              <NoSuggestionsTextField
                margin="dense"
                id="discountPrice"
                label="Precio con Descuento"
                type="text"
                fullWidth
                value={displayDiscountPrice}
                onChange={handleDiscountPriceChange}
                helperText="Opcional"
              />
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Switch de Producto Complejo */}
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              p: 2, borderRadius: '14px',
              border: isComplex ? '2px solid #1976d2' : '1.5px solid #e0e0e0',
              backgroundColor: isComplex ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
              transition: 'all 0.2s ease',
            }}>
              <Box>
                <Typography variant="body1" fontWeight={700} sx={{ color: isComplex ? '#1976d2' : '#333' }}>
                  Producto Complejo
                </Typography>
                <Typography variant="caption" sx={{ color: '#888' }}>
                  Tamaños, sabores, extras u otras opciones configurables
                </Typography>
              </Box>
              <Switch
                checked={isComplex}
                onChange={(e) => setIsComplex(e.target.checked)}
                color="primary"
                sx={{ transform: 'scale(1.1)' }}
              />
            </Box>

            {isComplex && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 1, py: 0.5,
                }}>
                  <Box sx={{ width: 4, height: 20, borderRadius: 2, bgcolor: '#1976d2' }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#444', fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Grupos de opciones
                  </Typography>
                </Box>

                {detailVariants.map((group, groupIndex) => (
                  <Box key={groupIndex} sx={{
                    borderRadius: '16px',
                    border: '1.5px solid #e3e8f0',
                    overflow: 'hidden',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  }}>
                    {/* Header del Grupo */}
                    <Box sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5,
                      px: 2, py: 1.5,
                      background: 'linear-gradient(135deg, #1976d208 0%, #1976d215 100%)',
                      borderBottom: '1px solid #e3e8f0',
                    }}>
                      <Box sx={{
                        width: 28, height: 28, borderRadius: '8px',
                        bgcolor: '#1976d2', color: '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.75rem', fontWeight: 900, flexShrink: 0,
                      }}>
                        {groupIndex + 1}
                      </Box>
                      <NoSuggestionsTextField
                        fullWidth
                        size="small"
                        label="Nombre del grupo (Ej: Tamaño, Sabor, Extras)"
                        value={group.option_display_name}
                        onChange={(e) => handleGroupTitleChange(groupIndex, e.target.value)}
                        sx={{
                          '& .MuiOutlinedInput-root': { borderRadius: '10px', backgroundColor: '#fff' },
                        }}
                      />
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemoveGroup(groupIndex)}
                        disabled={detailVariants.length === 1}
                        sx={{
                          border: '1.5px solid currentColor',
                          borderRadius: '8px',
                          opacity: detailVariants.length === 1 ? 0.3 : 1,
                          flexShrink: 0,
                        }}
                      >
                        <RemoveCircleOutlineIcon fontSize="small" />
                      </IconButton>
                    </Box>

                    {/* Opciones del grupo */}
                    <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {group.details.map((option, optionIndex) => (
                        <Box key={optionIndex} sx={{
                          display: 'flex', alignItems: 'center', gap: 1,
                          p: 1.5,
                          borderRadius: '12px',
                          backgroundColor: '#fafbfc',
                          border: '1px solid #eef0f3',
                          transition: 'border-color 0.15s',
                          '&:hover': { borderColor: '#1976d230' },
                        }}>
                          <Box sx={{
                            width: 6, height: 6, borderRadius: '50%',
                            bgcolor: '#1976d260', flexShrink: 0,
                          }} />
                          <NoSuggestionsTextField
                            fullWidth
                            size="small"
                            label="Opción (Ej: Grande, Extra queso)"
                            value={option.name}
                            onChange={(e) => handleOptionChange(groupIndex, optionIndex, 'name', e.target.value)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                          />
                          <NoSuggestionsTextField
                            size="small"
                            label="Costo extra"
                            placeholder="$ 0"
                            value={option.value}
                            onChange={(e) => handleOptionChange(groupIndex, optionIndex, 'value', e.target.value)}
                            sx={{ width: '130px', flexShrink: 0, '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                          />
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleRemoveOption(groupIndex, optionIndex)}
                            disabled={group.details.length === 1}
                            sx={{ flexShrink: 0, opacity: group.details.length === 1 ? 0.3 : 1 }}
                          >
                            <RemoveCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ))}

                      {/* Añadir opción */}
                      <Box
                        onClick={() => handleAddOption(groupIndex)}
                        sx={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1,
                          py: 1, borderRadius: '10px',
                          border: '1.5px dashed #1976d240',
                          cursor: 'pointer', color: '#1976d2',
                          transition: 'all 0.15s',
                          '&:hover': { backgroundColor: '#1976d208', borderColor: '#1976d280' },
                        }}
                      >
                        <AddCircleOutlineIcon fontSize="small" />
                        <Typography variant="caption" fontWeight={700}>Añadir opción</Typography>
                      </Box>
                    </Box>
                  </Box>
                ))}

                {/* Añadir Nuevo Grupo */}
                <Box
                  onClick={handleAddGroup}
                  sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
                    py: 1.5, borderRadius: '14px',
                    border: '2px dashed #1976d250',
                    cursor: 'pointer', color: '#1976d2',
                    transition: 'all 0.2s',
                    '&:hover': { backgroundColor: '#1976d20A', borderColor: '#1976d2' },
                  }}
                >
                  <AddCircleOutlineIcon />
                  <Typography variant="subtitle2" fontWeight={700}>Añadir Nuevo Grupo de Opciones</Typography>
                </Box>
              </Box>
            )}

            <Divider sx={{ my: 1 }} />

            {/* Imagen: preview centrada ENCIMA del botón */}
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>

              {previewUrl && (
                <Paper elevation={0} sx={{
                  width: '100%',
                  p: 2,
                  borderRadius: '16px',
                  backgroundColor: '#f9f9f9',
                  border: '1px dashed #d1d5db',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 1,
                }}>
                  <Typography variant="subtitle2" sx={{ color: '#444', fontWeight: 600 }}>
                    Vista previa
                  </Typography>
                  <Box
                    component="img"
                    src={previewUrl}
                    alt="Vista previa"
                    sx={{
                      maxWidth: '280px',
                      width: '100%',
                      height: 'auto',
                      maxHeight: '200px',
                      borderRadius: '12px',
                      objectFit: 'contain',
                      boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                    }}
                  />
                </Paper>
              )}

              <input
                id="product_image"
                name="product_image"
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="product_image" style={{ width: '100%' }}>
                <SendButton
                  component="span"
                  sx={{ width: '100%', padding: '12px' }}
                >
                  {previewUrl ? 'Cambiar imagen' : 'Subir Imagen'}
                </SendButton>
              </label>
              <Typography variant="caption" sx={{ color: '#6b6b6b', textAlign: 'center' }}>
                Seleccione una imagen (PNG, JPG, JPEG) - Opcional
              </Typography>
            </Box>
          </Box>
        </CustomDialogContent>
        <CustomDialogActions>
          <CloseButton onClick={handleClose} disabled={isSubmitting}>Cancelar</CloseButton>
          <SendButton onClick={handleSubmit} disabled={isSubmitting}>Guardar Producto</SendButton>
        </CustomDialogActions>
      </CustomDialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default CreateProductDialog;